import React from 'react';
import { RefreshCw, ListChecks, X, type LucideIcon } from 'lucide-react';
import CursorPagination from 'components/CursorPagination';
import ConsumerFilterBar from 'components/consumer/ConsumerFilterBar';
import {
  CONSUMER_TD_NO_CLASS,
  CONSUMER_TH_NO_CLASS,
  CONSUMER_TABLE_CLASS,
  CONSUMER_TBODY_CLASS,
  CONSUMER_THEAD_CLASS,
  consumerRowNumber,
} from 'components/consumer/consumer-list-styles';

type TableRowElement = React.ReactElement<{ children?: React.ReactNode }>;

function prependNoHeader(head: React.ReactNode): React.ReactNode {
  if (!React.isValidElement(head) || head.type !== 'tr') return head;
  const row = head as TableRowElement;
  return React.cloneElement(row, {}, [
    <th key="row-no" className={CONSUMER_TH_NO_CLASS}>No.</th>,
    ...(React.Children.toArray(row.props.children) as React.ReactNode[]),
  ]);
}

function injectNoCells(body: React.ReactNode, page: number, limit: number): React.ReactNode {
  let rowIndex = 0;

  const walk = (node: React.ReactNode): React.ReactNode => {
    if (!React.isValidElement(node)) return node;
    const element = node as TableRowElement;

    if (node.type === React.Fragment) {
      return React.cloneElement(
        element,
        {},
        React.Children.map(element.props.children, (child) => walk(child))
      );
    }

    if (node.type === 'tr') {
      const index = rowIndex++;
      return React.cloneElement(element, {}, [
        <td key="row-no" className={CONSUMER_TD_NO_CLASS}>
          {consumerRowNumber(page, index, limit)}
        </td>,
        ...(React.Children.toArray(element.props.children) as React.ReactNode[]),
      ]);
    }

    return node;
  };

  return walk(body);
}

interface ConsumerListPageLayoutProps {
  icon: LucideIcon;
  iconWrapClass?: string;
  title: string;
  description: string;
  totalCount: number | null;
  filteredCount: number;
  hasMoreRecords?: boolean;
  loading: boolean;
  refreshing: boolean;
  onRefresh: () => void;
  activeFilterCount: number;
  draftFilterCount: number;
  onApplyFilters: () => void;
  onClearFilters: () => void;
  filterFields: React.ReactNode;
  playerFilterLabel?: string;
  onClearPlayerFilter?: () => void;
  tableTitle: string;
  tableSubtitle?: string;
  emptyIcon: LucideIcon;
  emptyTitle: string;
  emptyDescription?: string;
  isDatasetEmpty: boolean;
  isFilteredEmpty: boolean;
  /** Data column count (excluding the auto-injected No. column). */
  colSpan: number;
  tableHead: React.ReactNode;
  tableBody: React.ReactNode;
  page: number;
  itemCount: number;
  limit: number;
  total: number;
  totalPages: number;
  onFirst: () => void;
  onPageChange: (page: number) => void;
  onLast: () => void;
}

const ConsumerListPageLayout: React.FC<ConsumerListPageLayoutProps> = ({
  icon: Icon,
  iconWrapClass = 'bg-indigo-500/10 text-indigo-600',
  title,
  description,
  totalCount,
  filteredCount,
  hasMoreRecords = false,
  loading,
  refreshing,
  onRefresh,
  activeFilterCount,
  draftFilterCount,
  onApplyFilters,
  onClearFilters,
  filterFields,
  playerFilterLabel,
  onClearPlayerFilter,
  tableTitle,
  tableSubtitle,
  emptyIcon: EmptyIcon,
  emptyTitle,
  emptyDescription,
  isDatasetEmpty,
  isFilteredEmpty,
  colSpan,
  tableHead,
  tableBody,
  page,
  itemCount,
  limit,
  total,
  totalPages,
  onFirst,
  onPageChange,
  onLast,
}) => (
  <div className="space-y-5 animate-fadeIn">
    <div className="rounded-2xl border border-line bg-card shadow-sm overflow-hidden">
      <div className="px-5 py-5 sm:px-7 sm:py-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className={`flex h-12 w-12 items-center justify-center rounded-xl shrink-0 ${iconWrapClass}`}>
            <Icon size={22} strokeWidth={2.25} />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-heading tracking-tight">{title}</h1>
            <p className="text-sm text-muted mt-0.5">{description}</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="rounded-lg border border-line bg-subtle/60 px-2.5 py-1 text-xs font-semibold text-body">
            {totalCount != null
              ? activeFilterCount > 0
                ? `${total.toLocaleString()} of ${totalCount.toLocaleString()}`
                : `${total.toLocaleString()} total`
              : `${total.toLocaleString()} total`}
          </span>
          <button
            type="button"
            onClick={onRefresh}
            disabled={loading || refreshing}
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-line text-muted hover:text-heading hover:bg-subtle transition-colors disabled:opacity-50"
            title="Refresh"
          >
            <RefreshCw size={15} className={loading || refreshing ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>
    </div>

    {playerFilterLabel != null && onClearPlayerFilter && (
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-xl border border-indigo-200/80 bg-indigo-500/10 px-4 py-3">
        <p className="text-sm text-body">
          Filtered for{' '}
          <span className="font-bold text-heading">{playerFilterLabel || 'selected player'}</span>
        </p>
        <button
          type="button"
          onClick={onClearPlayerFilter}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-line bg-card text-xs font-bold text-muted hover:text-heading hover:bg-subtle transition-colors shrink-0"
        >
          <X size={14} /> Clear player filter
        </button>
      </div>
    )}

    {!isDatasetEmpty && (
      <ConsumerFilterBar
        activeFilterCount={activeFilterCount}
        draftFilterCount={draftFilterCount}
        onApply={onApplyFilters}
        onClear={onClearFilters}
      >
        {filterFields}
      </ConsumerFilterBar>
    )}

    <div className="rounded-2xl border border-line bg-card shadow-sm overflow-hidden">
      {loading && isDatasetEmpty ? (
        <div className="flex h-48 items-center justify-center text-muted font-medium">
          <RefreshCw className="animate-spin mr-2" size={18} /> Loading…
        </div>
      ) : isDatasetEmpty ? (
        <div className="px-6 py-16 text-center">
          <EmptyIcon size={32} className="mx-auto text-muted/30 mb-3" />
          <p className="font-semibold text-heading">{emptyTitle}</p>
          {emptyDescription && <p className="text-sm text-muted mt-1 max-w-md mx-auto">{emptyDescription}</p>}
        </div>
      ) : (
        <>
          <div className="px-5 py-4 sm:px-6 border-b border-line flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h4 className="font-bold text-heading flex items-center gap-2">
                <ListChecks className="text-indigo-600" size={18} />
                {tableTitle}
              </h4>
              {tableSubtitle && <p className="text-xs text-muted mt-0.5">{tableSubtitle}</p>}
            </div>
            <span className="text-xs bg-subtle text-muted px-2.5 py-1 rounded-full font-semibold shrink-0">
              Page {page} of {totalPages}
            </span>
          </div>

          <div className="overflow-x-auto max-h-[62vh] overflow-y-auto">
            <table className={CONSUMER_TABLE_CLASS}>
              <thead className={CONSUMER_THEAD_CLASS}>{prependNoHeader(tableHead)}</thead>
              <tbody className={CONSUMER_TBODY_CLASS}>
                {isFilteredEmpty ? (
                  <tr>
                    <td colSpan={colSpan + 1} className="px-5 py-14 text-center">
                      <EmptyIcon size={28} className="mx-auto text-muted/30 mb-2" />
                      <p className="font-semibold text-body">No records match your filters</p>
                      <p className="text-sm text-muted mt-1">Adjust filters or clear them to see all records.</p>
                      {activeFilterCount > 0 && (
                        <button
                          type="button"
                          onClick={onClearFilters}
                          className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-line text-sm font-semibold text-muted hover:text-heading hover:bg-subtle transition-colors"
                        >
                          <X size={14} /> Clear filters
                        </button>
                      )}
                    </td>
                  </tr>
                ) : (
                  injectNoCells(tableBody, page, limit)
                )}
              </tbody>
            </table>
          </div>

          <CursorPagination
            page={page}
            totalPages={totalPages}
            total={total}
            itemCount={itemCount}
            limit={limit}
            loading={loading || refreshing}
            onFirst={onFirst}
            onPageChange={onPageChange}
            onLast={onLast}
          />
        </>
      )}
    </div>
  </div>
);

export default ConsumerListPageLayout;
